import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, QrCode, SquarePen, Archive, 
  CircleCheckBig, Clock, CircleAlert,
  ChevronDown, Paperclip, User as UserIcon, CheckCircle2,
  Loader2, X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { cn } from "../../components/ui/utils";
import { toast } from "sonner";
import { serviceDeskApi } from "../../utils/service-desk-api";
import { RetireFormModal } from "../../components/service-desk/RetireFormModal";
import { FormQRCodeModal } from "../../components/service-desk/FormQRCodeModal";
import { DynamicFormFields } from "../../components/service-desk/DynamicFormFields";

export default function ViewRequestForm() {
  const navigate = useNavigate();
  const { deskId, formId } = useParams<{ deskId: string, formId: string }>();
  
  const [form, setForm] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetireModalOpen, setIsRetireModalOpen] = useState(false);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [isEditingSLA, setIsEditingSLA] = useState(false);
  const [slaValue, setSlaValue] = useState<number>(48);
  const [slaUnit, setSlaUnit] = useState<'hours' | 'days'>('hours');
  const [isSavingSLA, setIsSavingSLA] = useState(false);

  const [isEditingAutoAssign, setIsEditingAutoAssign] = useState(false);
  const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
  const [selectedAutoAssigneeIds, setSelectedAutoAssigneeIds] = useState<string[]>([]);
  const [isAutoAssignModalOpen, setIsAutoAssignModalOpen] = useState(false);
  const [isSavingAutoAssign, setIsSavingAutoAssign] = useState(false);
  const [previewValues, setPreviewValues] = useState<Record<string, any>>({});

  const fetchFormDetails = async () => {
    if (!formId) return;
    setIsLoading(true);
    try {
      const response = await serviceDeskApi.getFormDetails(formId);
      if (response.success) {
        setForm(response.form);
        const hours = response.form.slaHours || 48;
        if (hours % 24 === 0) {
            setSlaValue(hours / 24);
            setSlaUnit('days');
        } else {
            setSlaValue(hours);
            setSlaUnit('hours');
        }

        const assignees = response.form.autoAssignees || [];
        setAutoAssignEnabled(assignees.length > 0);
        setSelectedAutoAssigneeIds(assignees.map((a: any) => a.id));
      } else {
        toast.error("Form not found.");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error fetching form:", error);
      toast.error("Failed to load form details.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFormDetails();
  }, [formId]);

  const handleRetire = async () => {
    if (!formId) return;
    
    try {
        const response = await serviceDeskApi.retireForm(formId);
        if (response.success) {
            toast.success("Form retired successfully.");
            navigate(-1);
        }
    } catch (error) {
        toast.error("Failed to retire form.");
    }
  };

  const handleSaveSLA = async () => {
    if (!formId) return;
    setIsSavingSLA(true);
    try {
        const totalHours = slaUnit === 'days' ? slaValue * 24 : slaValue;
        const response = await serviceDeskApi.updateForm(formId, { slaHours: totalHours });
        if (response.success) {
            setForm((prev: any) => ({ ...prev, slaHours: totalHours }));
            setIsEditingSLA(false);
            toast.success("SLA updated successfully.");
        }
    } catch (error) {
        toast.error("Failed to update SLA.");
    } finally {
        setIsSavingSLA(false);
    }
  };

  const handleSaveAutoAssign = async () => {
    if (!formId) return;
    setIsSavingAutoAssign(true);
    try {
        const autoAssigneeIds = autoAssignEnabled ? selectedAutoAssigneeIds : [];
        const response = await serviceDeskApi.updateForm(formId, { autoAssigneeIds });
        if (response.success) {
            setForm((prev: any) => ({ ...prev, autoAssignees: response.form.autoAssignees }));
            setIsEditingAutoAssign(false);
            toast.success("Auto-assignment settings updated.");
        }
    } catch (error) {
        toast.error("Failed to update auto-assignment.");
    } finally {
        setIsSavingAutoAssign(false);
    }
  };

  const toggleAutoAssignee = (userId: string) => {
    setSelectedAutoAssigneeIds(prev => 
        prev.includes(userId) 
            ? prev.filter(id => id !== userId) 
            : [...prev, userId]
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!form) return null;

  const latestVersion = (form.versions || []).sort((a: any, b: any) => b.versionNumber - a.versionNumber)[0];
  const fields = latestVersion?.fields || [];
  const status = latestVersion?.status || 'DRAFT';

  // Generate the direct link for the form
  const qrLink = `${window.location.origin}/service-desk/${deskId}/requests/new?form=${form.id}`;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white -m-6 h-[calc(100dvh-64px)]">
      {/* Header Section */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{form.name}</h1>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 font-medium">
                <span>Version {latestVersion?.versionNumber || 1}</span>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <CircleCheckBig className={cn("w-3.5 h-3.5", status === 'PUBLISHED' ? "text-green-600" : "text-blue-600")} />
                  <span className={cn(status === 'PUBLISHED' ? "text-green-600" : "text-blue-600")}>
                      {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                  </span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Last updated {new Date(form.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
                className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-accent transition-colors shadow-sm font-bold uppercase text-xs" 
                title="View Form QR Code"
                onClick={() => setIsQRCodeModalOpen(true)}
            >
              <QrCode className="w-4 h-4" />
              <span>QR Code</span>
            </button>
            <Button 
                className="gap-2 px-4 py-2 bg-primary text-white hover:opacity-90 transition-opacity font-bold uppercase text-xs h-9 shadow-md"
                onClick={() => navigate(`/service-desk/${deskId}/forms/${form.id}/edit`)}
            >
              <SquarePen className="w-4 h-4" />
              Edit Form
            </Button>
            <Button 
                variant="outline"
                className="gap-2 px-4 py-2 border border-destructive text-destructive hover:bg-destructive/10 transition-colors font-bold uppercase text-xs h-9"
                onClick={() => setIsRetireModalOpen(true)}
            >
              <Archive className="w-4 h-4" />
              Retire Form
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-8">
          {/* Form Information */}
          <div>
            <h3 className="text-lg font-bold mb-4">Form Information</h3>
            <div className="bg-card border border-border rounded-xl p-6 space-y-4 shadow-sm">
              <div>
                <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Form Name</label>
                <div className="font-bold text-gray-900">{form.name}</div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Description</label>
                <div className="text-gray-700 font-medium">{form.description || "No description provided."}</div>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Version</label>
                  <div className="font-bold text-gray-900">{latestVersion?.versionNumber || 1}</div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Created</label>
                  <div className="font-bold text-gray-900">{new Date(form.createdAt).toLocaleDateString()}</div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider mb-1">Last Updated</label>
                  <div className="font-bold text-gray-900">{new Date(form.updatedAt).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider">Service Level Agreement (SLA)</label>
                  {!isEditingSLA && (
                    <button 
                        onClick={() => setIsEditingSLA(true)}
                        className="text-xs font-bold text-primary hover:underline uppercase tracking-tight"
                    >
                        Edit
                    </button>
                  )}
                </div>
                
                {isEditingSLA ? (
                    <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="flex items-center gap-3">
                            <Input 
                                type="number" 
                                min="1" 
                                className="w-24 h-10 bg-gray-50 border-gray-200 focus:ring-primary/20 font-bold" 
                                value={slaValue}
                                onChange={(e) => setSlaValue(parseInt(e.target.value) || 1)}
                            />
                            <select 
                                className="h-10 px-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-bold text-gray-700 cursor-pointer"
                                value={slaUnit}
                                onChange={(e) => setSlaUnit(e.target.value as any)}
                            >
                                <option value="hours">Hours</option>
                                <option value="days">Days</option>
                            </select>
                            <span className="text-sm text-gray-400 font-medium">from submission</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                size="sm" 
                                className="h-8 px-4 font-bold uppercase text-[10px]" 
                                onClick={handleSaveSLA}
                                disabled={isSavingSLA}
                            >
                                {isSavingSLA ? "Saving..." : "Save"}
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-8 px-4 font-bold uppercase text-[10px] border-gray-200" 
                                onClick={() => setIsEditingSLA(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <CircleAlert className="w-4 h-4 text-gray-400" />
                        <span className="font-bold text-gray-900">{form.slaHours || 48} hours</span>
                        <span className="text-sm text-gray-500 font-medium">from request submission</span>
                    </div>
                )}
              </div>
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-bold text-gray-400 uppercase tracking-wider">Auto-Assignment</label>
                  {!isEditingAutoAssign && (
                    <button 
                        onClick={() => setIsEditingAutoAssign(true)}
                        className="text-xs font-bold text-primary hover:underline uppercase tracking-tight"
                    >
                        Edit
                    </button>
                  )}
                </div>

                {isEditingAutoAssign ? (
                    <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 cursor-pointer group w-fit">
                                <Checkbox 
                                    id="auto-assign-toggle" 
                                    checked={autoAssignEnabled} 
                                    onCheckedChange={(checked) => setAutoAssignEnabled(checked as boolean)}
                                />
                                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">Enable auto-assignment for new requests</span>
                            </label>
                            
                            {autoAssignEnabled && (
                                <div className="space-y-2 pl-6 animate-in fade-in slide-in-from-left-1 duration-200">
                                    <p className="text-xs text-muted-foreground font-medium">Select team members who can be auto-assigned.</p>
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={() => setIsAutoAssignModalOpen(true)}
                                            className="text-sm text-primary font-bold hover:underline flex items-center gap-1.5"
                                        >
                                            Select team members
                                        </button>
                                        {selectedAutoAssigneeIds.length > 0 && (
                                            <span className="text-xs text-muted-foreground font-medium">
                                                {selectedAutoAssigneeIds.length} member(s) selected
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                            <Button 
                                size="sm" 
                                className="h-8 px-4 font-bold uppercase text-[10px]" 
                                onClick={handleSaveAutoAssign}
                                disabled={isSavingAutoAssign || (autoAssignEnabled && selectedAutoAssigneeIds.length === 0)}
                            >
                                {isSavingAutoAssign ? "Saving..." : "Save"}
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-8 px-4 font-bold uppercase text-[10px] border-gray-200" 
                                onClick={() => {
                                    setIsEditingAutoAssign(false);
                                    const assignees = form.autoAssignees || [];
                                    setAutoAssignEnabled(assignees.length > 0);
                                    setSelectedAutoAssigneeIds(assignees.map((a: any) => a.id));
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 font-bold uppercase">
                            {form.autoAssignees && form.autoAssignees.length > 0 ? (
                                <div className="flex items-center gap-2 text-gray-900">
                                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center text-[8px] text-white font-bold">
                                        {form.autoAssignees.length}
                                    </div>
                                    <span>Workload balanced among {form.autoAssignees.length} members</span>
                                </div>
                            ) : "Disabled"}
                        </span>
                    </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Form Fields</h3>
              <span className="text-sm font-bold text-gray-400 uppercase tracking-wider">{fields.length} fields</span>
            </div>
            <div className="space-y-3">
              {fields.map((field: any, index: number) => (
                <div key={field.id} className="bg-card border border-border rounded-xl p-4 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-bold text-gray-500">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-bold text-gray-900">{field.label}</span>
                        {!['heading', 'static_text'].includes(field.type) && field.required && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 font-bold uppercase rounded-sm">Required</span>}
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 font-bold uppercase rounded-sm">{field.type.replace('_', ' ')}</span>
                      </div>
                      {field.description && (
                        <div className={cn(
                          "text-sm mb-2 font-medium",
                          field.type === 'static_text' 
                            ? (field.showContainer 
                                ? cn(
                                    "p-3 rounded-lg border whitespace-pre-wrap",
                                    field.containerColor === 'blue' ? "bg-blue-50/50 border-blue-100 text-blue-800" :
                                    field.containerColor === 'green' ? "bg-green-50/50 border-green-100 text-green-800" :
                                    field.containerColor === 'amber' ? "bg-amber-50/50 border-amber-100 text-amber-800" :
                                    field.containerColor === 'red' ? "bg-red-50/50 border-red-100 text-red-800" :
                                    "bg-gray-50/50 border-gray-100 text-gray-800"
                                  )
                                : "text-gray-600 whitespace-pre-wrap"
                              )
                            : "text-gray-500"
                        )}>
                          {field.description}
                        </div>
                      )}
                      {!['heading', 'static_text'].includes(field.type) && field.placeholder && <div className="text-sm text-gray-400 italic font-medium">Placeholder: "{field.placeholder}"</div>}
                      {field.options && field.options.length > 0 && (
                          <div className="mt-3">
                              <div className="text-[10px] font-bold text-gray-400 uppercase mb-2">Options:</div>
                              <div className="space-y-1">
                                  {field.options.map((opt: string, i: number) => (
                                      <div key={i} className="text-sm pl-3 border-l-2 border-gray-200 font-medium text-gray-600">{opt}</div>
                                  ))}
                              </div>
                          </div>
                      )}
                      {field.type === 'idarah_data' && (
                          <div className="mt-3 p-3 bg-blue-50/50 rounded-lg border border-blue-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-blue-700 uppercase tracking-tight">Source Table:</span>
                                <Badge variant="secondary" className="bg-blue-100 text-blue-700 font-black uppercase text-[10px] tracking-widest">{field.dataSource}</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-blue-700 uppercase tracking-tight">Mode:</span>
                                <span className="text-xs font-medium text-blue-600 italic capitalize">{field.selectionMode} Select</span>
                            </div>
                          </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {fields.length === 0 && (
                  <div className="p-8 border border-dashed border-gray-200 rounded-xl text-center text-gray-400 font-medium">
                      No fields configured for this version.
                  </div>
              )}
            </div>
          </div>

          {/* Form Preview */}
          <div>
            <h3 className="text-lg font-bold mb-4">Form Preview</h3>
            <div className="bg-gray-50/50 border border-border border-dashed rounded-2xl p-8 shadow-inner">
              <div className="bg-white rounded-2xl p-8 space-y-8 shadow-md border border-border text-left max-w-2xl mx-auto">
                <div>
                  <h2 className="text-2xl font-bold mb-2 text-gray-900">{form.name}</h2>
                  <p className="text-gray-500 font-medium leading-relaxed">{form.description}</p>
                </div>
                
                <DynamicFormFields 
                    fields={fields}
                    values={previewValues}
                    onChange={(id, val) => setPreviewValues(prev => ({ ...prev, [id]: val }))}
                    isPreview={true}
                    showSectionTitle={false}
                />
                
                <div className="pt-6 border-t border-border">
                    <Button className="w-full h-12 bg-[#0f172a] text-white text-base font-bold opacity-50 cursor-not-allowed rounded-xl shadow-lg shadow-[#0f172a]/10" disabled>
                        Submit Request
                    </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RetireFormModal 
        isOpen={isRetireModalOpen}
        onClose={() => setIsRetireModalOpen(false)}
        onConfirm={handleRetire}
        formName={form.name}
      />

      <FormQRCodeModal
        isOpen={isQRCodeModalOpen}
        onClose={() => setIsQRCodeModalOpen(false)}
        deskName={form.serviceDesk?.name || "Service Desk"}
        formName={form.name}
        qrLink={qrLink}
      />

      {/* Auto-Assign Member Modal */}
      <Dialog open={isAutoAssignModalOpen} onOpenChange={setIsAutoAssignModalOpen}>
        <DialogContent hideClose={true} className="p-0 sm:max-w-md border-none overflow-hidden bg-card border-border shadow-2xl rounded-lg">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Select Team Members</h3>
                <button 
                    onClick={() => setIsAutoAssignModalOpen(false)}
                    className="p-1 hover:bg-accent rounded-lg transition-colors text-gray-400 hover:text-gray-900"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4 font-medium">Select team members eligible for auto-assignment. New requests will be automatically assigned to the member with the fewest active requests for balanced workload distribution.</p>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {(form.serviceDesk?.members || []).map((member: any) => (
                    <label key={member.id} className="flex items-center gap-3 p-3 hover:bg-accent rounded-lg cursor-pointer transition-colors">
                        <input 
                            type="checkbox" 
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20"
                            checked={selectedAutoAssigneeIds.includes(member.user?.id)}
                            onChange={() => toggleAutoAssignee(member.user?.id)}
                        />
                        <div className="flex items-center gap-2 flex-1">
                            <div className="w-6 h-6 text-xs bg-blue-500 rounded-full flex items-center justify-center text-white font-medium" title={member.user?.name}>
                                {member.user?.name?.split(' ').map((n: any) => n[0]).join('')}
                            </div>
                            <div>
                                <div className="text-sm font-medium">{member.user?.name}</div>
                                <div className="text-xs text-muted-foreground">{member.user?.email}</div>
                            </div>
                        </div>
                        <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary font-bold uppercase rounded">{member.role}</span>
                    </label>
                ))}
                {(form.serviceDesk?.members || []).length === 0 && (
                    <p className="text-sm text-gray-400 italic text-center py-8">No team members found for this desk.</p>
                )}
            </div>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border">
                <button 
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground font-bold rounded-lg hover:opacity-90 transition-opacity"
                    onClick={() => setIsAutoAssignModalOpen(false)}
                >
                    Done
                </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}